// src/pages/TimesheetList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { timesheetAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Plus, RefreshCw, Calendar, ChevronRight, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';
import './TimesheetList.css';

const MOIS_LABELS = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];

const STATUT_META = {
  BROUILLON: { label: 'Brouillon',  icon: Clock,        color: '#8B8FA8', bg: 'rgba(139,143,168,0.12)' },
  SOUMIS:    { label: 'Soumis',     icon: AlertCircle,  color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  VALIDE:    { label: 'Validé',     icon: CheckCircle,  color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  REJETE:    { label: 'Rejeté',     icon: XCircle,      color: '#DC2626', bg: 'rgba(239,68,68,0.12)' },
};

const RH_ROLES = ['SUPER_ADMIN', 'ADMIN', 'RH'];

export default function TimesheetList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRH = RH_ROLES.includes(user?.role);

  const currentDate = new Date();
  const [annee, setAnnee]   = useState(currentDate.getFullYear());
  const [mois,  setMois]    = useState(currentDate.getMonth() + 1);
  const [statut, setStatut] = useState('');
  const [items,  setItems]  = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { annee, user_id: user?.id };
      if (mois)   params.mois   = mois;
      if (statut) params.statut = statut;
      const res = await timesheetAPI.list(params);
      setItems(res.data || []);
    } catch { toast.error('Erreur chargement feuilles de temps'); }
    finally { setLoading(false); }
  }, [annee, mois, statut]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenMonth = async () => {
    try {
      await timesheetAPI.getMine(annee, mois);
      navigate(`/timesheets/${annee}/${mois}`);
    } catch {
      toast.error('Erreur ouverture feuille de temps');
    }
  };

  const yearOptions = [];
  for (let y = currentDate.getFullYear() - 1; y <= currentDate.getFullYear() + 1; y++) {
    yearOptions.push(y);
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Feuilles de temps</h1>
          <p>{isRH ? 'Gestion et validation des feuilles de temps de l\'équipe' : 'Saisie et suivi de vos feuilles de temps mensuelles'}</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/></button>
          <button className="btn btn-primary" onClick={() => navigate(`/timesheets/${new Date().getFullYear()}/${new Date().getMonth() + 1}`)}>
            <Calendar size={15}/> Mois en cours
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <select className="filter-select" value={annee} onChange={e => setAnnee(+e.target.value)}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="filter-select" value={mois} onChange={e => setMois(+e.target.value)}>
          <option value="">Tous les mois</option>
          {MOIS_LABELS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select className="filter-select" value={statut} onChange={e => setStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner"/></div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} color="var(--text-faint)"/>
          <h3>Aucune feuille de temps</h3>
          <p>{isRH ? 'Aucune feuille soumise pour cette période.' : 'Ouvrez votre feuille de temps pour commencer la saisie.'}</p>
          {!isRH && (
            <button className="btn btn-primary" onClick={handleOpenMonth}>
              <Plus size={15}/> Ouvrir {MOIS_LABELS[mois - 1]} {annee}
            </button>
          )}
        </div>
      ) : (
        <div className="ts-list">
          {items.map(ts => {
            const meta = STATUT_META[ts.statut] || STATUT_META.BROUILLON;
            const StatusIcon = meta.icon;
            const totalJours = ts.lines.reduce((acc, l) =>
              acc + l.days.reduce((a, d) => a + (d.valeur || 0), 0), 0
            );
            return (
              <div key={ts.id} className="ts-card" onClick={() => navigate(`/timesheets/${ts.annee}/${ts.mois}`)}>
                <div className="ts-card-left">
                  <div className="ts-card-month">
                    <span className="ts-month-num">{String(ts.mois).padStart(2, '0')}</span>
                    <span className="ts-month-year">{ts.annee}</span>
                  </div>
                  <div className="ts-card-info">
                    <div className="ts-card-name">
                      {MOIS_LABELS[ts.mois - 1]} {ts.annee}
                      {isRH && ts.user_prenom && (
                        <span className="ts-card-user"> — {ts.user_prenom} {ts.user_nom}</span>
                      )}
                    </div>
                    <div className="ts-card-meta">
                      <span>{totalJours.toFixed(1)} jour{totalJours !== 1 ? 's' : ''} saisis</span>
                      {ts.date_validation && (
                        <span>Validé le {new Date(ts.date_validation).toLocaleDateString('fr-FR')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ts-card-right">
                  <span className="badge" style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}33` }}>
                    <StatusIcon size={12}/>
                    {meta.label}
                  </span>
                  <ChevronRight size={16} color="var(--text-faint)"/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
